import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertCircle, Sparkles, CalendarClock, ArrowUp, Loader2, Zap, Infinity as InfinityIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = {
    free: '🌱 Explorer', essentiel: '🌿 Essentiel', pro: '💼 Impact',
    organisation: '🏢 Organisation', unlimited: '🏛️ Institution',
    starter: '🚀 Starter',
};

const BILLING_LABEL = {
    none: '',
    monthly: 'mensuel',
    yearly: 'annuel · par an',
    one_time: 'paiement unique',
};

const UPGRADE_TIERS = [
    { tier: 'free', emoji: '🌱', name: 'Explorer', price: 'Gratuit · permanent', perks: '1 organisation · 1 projet actif · 3 utilisateurs · IA 20 req/mois' },
    { tier: 'essentiel', emoji: '🌿', name: 'Essentiel', price: '39 €/mois', perks: '3 projets · 10 utilisateurs · IA 50 req/mois · support 48 h' },
    { tier: 'pro', emoji: '💼', name: 'Impact', price: '99 €/mois', perks: '3 organisations · 10 projets · 25 utilisateurs · IA 300 req/mois · marque blanche & kits inclus', recommended: true },
    { tier: 'organisation', emoji: '🏢', name: 'Organisation', price: '249 €/mois', perks: '10 organisations · projets illimités · 100 utilisateurs · IA 1 000 req/mois · dashboards consolidés' },
    { tier: 'unlimited', emoji: '🏛️', name: 'Institution', price: 'Sur devis', perks: 'Capacité illimitée · offre Consortium incluse · SLA · support dédié' },
];

/**
 * Quota usage banner — shown on Dashboard.
 * Silently no-op if backend returns 401/error (e.g. plan endpoint not yet seeded).
 */
export default function QuotaBanner() {
    const { session } = useAuth();
    const [info, setInfo] = useState(null);
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [buyingOption, setBuyingOption] = useState(null);

    useEffect(() => {
        if (!session) return;
        axios.get(`${API}/account-plans/me`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
        })
            .then(r => setInfo(r.data))
            .catch(() => { /* silent */ });
    }, [session]);

    const buyOption = async (optionId) => {
        setBuyingOption(optionId);
        try {
            const r = await axios.post(`${API}/billing/stripe/checkout-option`,
                { option_id: optionId, origin_url: window.location.origin },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
            setBuyingOption(null);
        }
    };

    if (!info?.plan) return null;
    const { plan, usage, overage, days_left } = info;
    const isFree = plan.plan_tier === 'free';

    const Bar = ({ label, used, max }) => {
        const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
        const color = pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-amber-500' : 'bg-secondary';
        return (
            <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground/65">{label}</span>
                    <span className="font-mono text-foreground/85">{used} / {max >= 9999 ? '∞' : max}</span>
                </div>
                <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
                    <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                </div>
            </div>
        );
    };

    // Countdown styling — calmer on >60d, amber 30-60d, red <30d, expired = strong red
    const expired = days_left !== null && days_left !== undefined && days_left < 0;
    const danger = days_left !== null && days_left !== undefined && days_left >= 0 && days_left <= 30;
    const warn = days_left !== null && days_left !== undefined && days_left > 30 && days_left <= 60;

    let countdownClasses = 'border-border bg-surface-muted/40 text-foreground/80';
    if (expired) countdownClasses = 'border-destructive/40 bg-destructive/10 text-destructive';
    else if (danger) countdownClasses = 'border-destructive/30 bg-destructive/5 text-destructive';
    else if (warn) countdownClasses = 'border-amber-300 bg-amber-50/60 text-amber-900';

    const periodEndDate = (!isFree && plan.period_end)
        ? new Date(plan.period_end).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
        : null;

    return (
        <div className={`card-soft p-5 ${overage ? 'border-amber-300 bg-amber-50/40' : ''}`} data-testid="quota-banner">
            <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 mb-1">
                        <TrendingUp size={14} className="text-primary" />
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Plan actif</span>
                    </div>
                    <div className="font-heading text-lg" data-testid="quota-plan-name">
                        {TIER_LABEL[plan.plan_tier] || plan.plan_tier}
                        {isFree ? (
                            <span className="text-sm text-secondary font-normal ml-2 inline-flex items-center gap-1" data-testid="quota-free-permanent">
                                <InfinityIcon size={13} /> gratuit en permanence
                            </span>
                        ) : plan.billing_period && plan.billing_period !== 'none' && (
                            <span className="text-sm text-muted-foreground font-normal ml-2">
                                · {BILLING_LABEL[plan.billing_period] || plan.billing_period}
                            </span>
                        )}
                    </div>
                    {plan.organization && <div className="text-xs text-muted-foreground">{plan.organization}</div>}
                </div>
                <div className="grid grid-cols-3 gap-5 flex-1 min-w-[400px]">
                    <Bar label="Organisations" used={usage.workspaces} max={plan.max_workspaces} />
                    <Bar label="Projets" used={usage.projects} max={plan.max_projects} />
                    <Bar label="Utilisateurs" used={usage.users} max={plan.max_users} />
                </div>
                {plan.plan_tier !== 'unlimited' && (
                    <Button
                        onClick={() => setShowUpgrade(true)}
                        size="sm"
                        variant="outline"
                        className="border-primary/40 text-primary hover:bg-primary-light"
                        data-testid="quota-upgrade-cta"
                    >
                        <ArrowUp size={13} className="mr-1.5" /> Upgrade plan
                    </Button>
                )}
            </div>

            {periodEndDate && (
                <div
                    className={`mt-4 pt-4 border-t flex items-center gap-3 flex-wrap text-sm ${countdownClasses}`}
                    data-testid="subscription-countdown"
                    style={{ borderTopWidth: 1, paddingLeft: 0 }}
                >
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border ${countdownClasses}`}>
                        <CalendarClock size={14} />
                        {expired ? (
                            <span className="font-medium">
                                Abonnement expiré le {periodEndDate}
                            </span>
                        ) : (
                            <span>
                                <strong className="font-mono">{days_left}</strong> jour{days_left > 1 ? 's' : ''} avant l'échéance
                                <span className="opacity-70 ml-1.5">— fin le {periodEndDate}</span>
                            </span>
                        )}
                    </div>
                    {(warn || danger || expired) && (
                        <Link
                            to="/p/contact"
                            className="text-primary hover:underline inline-flex items-center gap-1 font-medium ml-auto"
                            data-testid="renew-link"
                        >
                            <Sparkles size={14} /> Renouveler / Discuter →
                        </Link>
                    )}
                </div>
            )}

            {overage && (
                <div className="mt-4 pt-4 border-t border-amber-200 flex items-center gap-3 flex-wrap text-sm">
                    <AlertCircle size={16} className="text-amber-700" />
                    <span className="text-amber-900 flex-1 min-w-[200px]">
                        Vous avez dépassé un ou plusieurs quotas de votre plan.
                    </span>
                    <Link to="/p/contact" className="text-primary hover:underline inline-flex items-center gap-1 font-medium" data-testid="quota-upgrade-link">
                        <Sparkles size={14} /> Discuter d'un upgrade →
                    </Link>
                </div>
            )}

            <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Faire évoluer votre plan</DialogTitle>
                        <DialogDescription>
                            Plan actuel : <strong>{TIER_LABEL[plan.plan_tier] || plan.plan_tier}</strong>. Choisissez l'offre qui correspond à vos besoins.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="upgrade-plans-grid">
                        {UPGRADE_TIERS.map(p => {
                            const isCurrent = p.tier === plan.plan_tier;
                            return (
                                <div key={p.tier}
                                    className={`p-5 rounded-lg border ${isCurrent ? 'border-primary bg-primary-light/50' : 'border-border hover:border-primary/40'} transition-all`}
                                    data-testid={`upgrade-plan-${p.tier}`}
                                >
                                    <div className="flex items-baseline justify-between mb-2">
                                        <div className="font-heading text-lg">{p.emoji} {p.name}</div>
                                        {isCurrent
                                            ? <span className="text-[10px] uppercase tracking-wider text-primary font-bold">Actuel</span>
                                            : p.recommended && <span className="text-[10px] uppercase tracking-wider text-secondary font-bold">Recommandé</span>}
                                    </div>
                                    <div className="text-xl font-mono text-foreground/85">{p.price}</div>
                                    <p className="text-xs text-foreground/70 mt-3 leading-relaxed">{p.perks}</p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-6 pt-5 border-t border-border">
                        <h4 className="font-heading text-base mb-1 flex items-center gap-2"><Zap size={15} className="text-secondary" /> Options à l'unité — activation instantanée</h4>
                        <p className="text-xs text-muted-foreground mb-3">Paiement unique sécurisé Stripe. L'option est active dès la confirmation du paiement.</p>
                        <div className="grid sm:grid-cols-3 gap-3" data-testid="options-instant-grid">
                            {[
                                { id: 'option-whitelabel', label: 'Marque blanche', price: '100 €' },
                                { id: 'option-spaces', label: '+2 espaces', price: '50 €' },
                                { id: 'option-users', label: '+5 utilisateurs', price: '50 €' },
                            ].map(o => (
                                <Button key={o.id} variant="outline" disabled={!!buyingOption}
                                    onClick={() => buyOption(o.id)}
                                    className="justify-between h-auto py-2.5"
                                    data-testid={`buy-${o.id}`}>
                                    <span className="text-sm">{o.label}</span>
                                    {buyingOption === o.id
                                        ? <Loader2 size={13} className="animate-spin" />
                                        : <span className="font-mono text-xs text-secondary">{o.price}</span>}
                                </Button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-3 mt-6">
                        <Link to="/p/tarifs" className="text-sm text-primary hover:underline" data-testid="upgrade-see-tarifs">Voir toutes les offres →</Link>
                        <Link to="/p/contact" onClick={() => setShowUpgrade(false)}>
                            <Button className="bg-primary hover:bg-primary-hover" data-testid="upgrade-contact-btn">
                                <Sparkles size={14} className="mr-2" /> Contacter le Super Admin
                            </Button>
                        </Link>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
